import { z } from "zod";
import { evaluateDeadline } from "./deadline";
import { holderFields } from "./contractSchema";
import { onlyDigits, parseBRL } from "./masks";
import { isValidCnpj, isValidCPF } from "./validators";

const step1Fields = {
  instituicao: z.string().min(1, "Selecione a instituição."),
  instituicaoOutra: z.string(),
  valorBloqueado: z.string().refine((v) => parseBRL(v) > 0, "Informe um valor maior que zero."),
  dataBloqueio: z
    .string()
    .min(1, "Informe a data do bloqueio.")
    .refine((v) => evaluateDeadline(v).status !== "invalid", "Data inválida.")
    .refine((v) => evaluateDeadline(v).status !== "future", "A data do bloqueio não pode ser futura."),
  motivoAlegado: z.string().trim().min(1, "Descreva o motivo alegado pela instituição.").max(500, "Máximo de 500 caracteres."),
  identificadorConta: z.string().trim().min(1, "Informe o e-mail de contato para notificação.").max(120, "Máximo de 120 caracteres.").email("E-mail inválido."),
};

const step2Strict = {
  tipoTitular: z.string().refine((v): boolean => v === "PF" || v === "PJ", "Selecione o tipo de titular."),
  razaoSocialOuNome: z.string().trim().min(2, "Informe o nome ou a razão social."),
  documentoTitular: z.string().min(1, "Informe o CPF ou CNPJ."),
  ...holderFields,
};

const step2Loose = {
  tipoTitular: z.string(),
  razaoSocialOuNome: z.string(),
  documentoTitular: z.string(),
  email: z.string(),
  whatsapp: z.string(),
  aceite: z.boolean(),
};

const refineOutro = (data: { instituicao: string; instituicaoOutra: string }, ctx: z.RefinementCtx): void => {
  if (data.instituicao === "Outro" && data.instituicaoOutra.trim().length < 2) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["instituicaoOutra"], message: "Informe o nome da instituição." });
  }
};

export function documentoError(tipo: string, doc: string): string | null {
  const len = onlyDigits(doc).length;
  if (tipo === "PF") {
    if (len === 14) return "Para pessoa física, informe um CPF.";
    return isValidCPF(doc) ? null : "CPF inválido. Verifique os dígitos.";
  }
  if (len === 11) return "Para pessoa jurídica, informe um CNPJ.";
  return isValidCnpj(doc) ? null : "CNPJ inválido. Verifique os dígitos.";
}

export const saldoStep1Schema = z.object({ ...step1Fields, ...step2Loose }).superRefine(refineOutro);
export const saldoSchema = z.object({ ...step1Fields, ...step2Strict }).superRefine((data, ctx) => {
  refineOutro(data, ctx);
  const msg = documentoError(data.tipoTitular, data.documentoTitular);
  if (msg) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["documentoTitular"], message: msg });
});

export type SaldoFormData = z.infer<typeof saldoSchema>;
export type SaldoFieldName = keyof SaldoFormData;

export const emptySaldo: SaldoFormData = {
  instituicao: "",
  instituicaoOutra: "",
  valorBloqueado: "",
  dataBloqueio: "",
  motivoAlegado: "",
  identificadorConta: "",
  tipoTitular: "PJ",
  razaoSocialOuNome: "",
  documentoTitular: "",
  email: "",
  whatsapp: "",
  aceite: false,
};

export const isSaldoField = (name: string): name is SaldoFieldName => name in emptySaldo;
